import React, { useEffect, useState, useCallback } from "react";
import AdminShell from "@/components/AdminShell";
import { getToken } from "@/lib/auth";

const PROTECTED_EMAIL = "anthonyemcdonald@gmail.com";
const PROTECTED_ROLES = ["owner", "super_admin"];

function isProtected(u) {
  return u.email === PROTECTED_EMAIL || PROTECTED_ROLES.includes(u.role);
}

export default function AdminUsers() {
  const [users, setUsers]             = useState([]);
  const [search, setSearch]           = useState("");
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [checked, setChecked]         = useState(new Set());
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [bulkBusy, setBulkBusy]       = useState(false);
  const [bulkMsg, setBulkMsg]         = useState("");

  const loadUsers = useCallback(() => {
    setLoading(true);
    const token = getToken();
    fetch("/api/admin/users", { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => { setUsers(data); setLoading(false); setChecked(new Set()); })
      .catch((e) => { setError("Failed to load users: " + e); setLoading(false); });
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const filtered = users.filter((u) => {
    const q = search.toLowerCase();
    return (
      (u.name  || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q) ||
      (u.city  || "").toLowerCase().includes(q) ||
      (u.state || "").toLowerCase().includes(q)
    );
  });

  const checkable   = filtered.filter((u) => !isProtected(u));
  const allSelected = checkable.length > 0 && checkable.every((u) => checked.has(u.id));

  const toggleAll = () => {
    setChecked((prev) => {
      const n = new Set(prev);
      if (allSelected) checkable.forEach((u) => n.delete(u.id));
      else checkable.forEach((u) => n.add(u.id));
      return n;
    });
  };

  const toggleOne = (id) =>
    setChecked((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const handleStatusChange = (userId, newStatus) =>
    setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, account_status: newStatus } : u));

  const handleUserDeleted = (userId) => {
    setUsers((prev) => prev.filter((u) => u.id !== userId));
    setSelectedUser(null);
  };

  const bulkDelete = async () => {
    setBulkBusy(true); setBulkMsg("");
    try {
      const token = getToken();
      const res = await fetch("/api/admin/users/bulk-delete", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ user_ids: [...checked] }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed");
      setBulkMsg(
        `Deleted ${data.deleted_count} user${data.deleted_count !== 1 ? "s" : ""}.` +
        (data.skipped_ids?.length ? ` Skipped ${data.skipped_ids.length} protected.` : "")
      );
      loadUsers();
    } catch (e) { setBulkMsg("Error: " + e.message); }
    finally { setBulkBusy(false); setConfirmBulk(false); }
  };

  return (
    <AdminShell>
      <div className="p-6 md:p-10 text-white">
        <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-2">Admin</div>
        <h1 className="font-display text-3xl mb-6">Users</h1>

        <div className="flex items-center gap-4 mb-4 flex-wrap">
          <input
            type="text"
            placeholder="Search by name, email, city, state…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full max-w-md px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/10 text-white placeholder:text-white/30 outline-none focus:border-[rgba(212,175,55,0.4)]"
          />
          <span className="text-white/40 text-sm whitespace-nowrap">{filtered.length} users</span>
          {checked.size > 0 && (
            <button
              onClick={() => setConfirmBulk(true)}
              className="ml-auto px-4 py-2 rounded-xl bg-red-500/15 hover:bg-red-500/25 text-red-400 border border-red-500/20 text-sm font-medium transition-colors"
            >
              Delete Selected ({checked.size})
            </button>
          )}
        </div>

        {bulkMsg && (
          <p className={`text-sm mb-3 ${bulkMsg.startsWith("Error") ? "text-red-400" : "text-emerald-400"}`}>{bulkMsg}</p>
        )}
        {loading && <div className="text-white/40">Loading…</div>}
        {error   && <div className="text-red-400">{error}</div>}

        {!loading && !error && (
          <div className="overflow-x-auto rounded-2xl border border-white/8">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-white/40 text-[11px] uppercase tracking-wider border-b border-white/8">
                  <th className="px-3 py-3 text-left">
                    <input type="checkbox" checked={allSelected} onChange={toggleAll} className="accent-[#D4AF37]" />
                  </th>
                  {["Name","Email","Role","City","State","ZIP","Gender","Age","Active","Onboarding","Joined"].map((h) => (
                    <th key={h} className="px-4 py-3 text-left font-medium whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => {
                  const age = (() => {
                    if (u.birth_month && u.birth_day && u.birth_year) {
                      const ms = Date.now() - new Date(
                        parseInt(u.birth_year),
                        parseInt(u.birth_month) - 1,
                        parseInt(u.birth_day)
                      ).getTime();
                      return Math.floor(ms / (365.25 * 24 * 60 * 60 * 1000));
                    }
                    return u.age || "—";
                  })();
                  const prot = isProtected(u);
                  return (
                    <tr key={u.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                      <td className="px-3 py-3">
                        {!prot && (
                          <input
                            type="checkbox"
                            checked={checked.has(u.id)}
                            onChange={() => toggleOne(u.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="accent-[#D4AF37]"
                          />
                        )}
                      </td>
                      <td className="px-4 py-3 font-medium cursor-pointer" onClick={() => setSelectedUser(u)}>{u.name || "—"}</td>
                      <td className="px-4 py-3 text-white/60 cursor-pointer" onClick={() => setSelectedUser(u)}>{u.email || "—"}</td>
                      <td className="px-4 py-3 cursor-pointer" onClick={() => setSelectedUser(u)}>
                        <span className={`text-[11px] px-2 py-0.5 rounded-full ${prot || u.role === "admin" ? "bg-[rgba(212,175,55,0.15)] text-[#F2DE94]" : "bg-white/5 text-white/50"}`}>
                          {u.role || "user"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-white/60 cursor-pointer" onClick={() => setSelectedUser(u)}>{u.city  || "—"}</td>
                      <td className="px-4 py-3 text-white/60 cursor-pointer" onClick={() => setSelectedUser(u)}>{u.state || "—"}</td>
                      <td className="px-4 py-3 text-white/60 cursor-pointer" onClick={() => setSelectedUser(u)}>{u.zip   || "—"}</td>
                      <td className="px-4 py-3 text-white/60 capitalize cursor-pointer" onClick={() => setSelectedUser(u)}>{u.gender || "—"}</td>
                      <td className="px-4 py-3 text-white/60 cursor-pointer" onClick={() => setSelectedUser(u)}>{age}</td>
                      <td className="px-4 py-3 cursor-pointer" onClick={() => setSelectedUser(u)}>
                        {u.account_status === "disabled" ? (
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-500/15 text-red-400">Disabled</span>
                        ) : (
                          <span className={`h-2 w-2 rounded-full inline-block ${u.active ? "bg-emerald-400" : "bg-white/20"}`} />
                        )}
                      </td>
                      <td className="px-4 py-3 cursor-pointer" onClick={() => setSelectedUser(u)}>
                        <span className={`text-[11px] ${u.onboarding_complete ? "text-emerald-400" : "text-white/30"}`}>
                          {u.onboarding_complete ? "Done" : "Pending"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-white/40 text-xs cursor-pointer" onClick={() => setSelectedUser(u)}>
                        {u.created_at ? u.created_at.slice(0, 10) : "—"}
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan="12" className="px-4 py-8 text-center text-white/30">No users found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedUser && (
        <UserModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onStatusChange={handleStatusChange}
          onDelete={handleUserDeleted}
        />
      )}

      {confirmBulk && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="glass rounded-2xl p-8 max-w-sm w-full text-white">
            <h2 className="font-display text-xl mb-3">
              Delete {checked.size} {checked.size === 1 ? "user" : "users"}?
            </h2>
            <p className="text-white/50 text-sm mb-6">
              This cannot be undone. Protected accounts will be skipped automatically.
            </p>
            <div className="flex gap-3">
              <button onClick={bulkDelete} disabled={bulkBusy}
                className="flex-1 py-2.5 rounded-xl bg-red-500/15 hover:bg-red-500/25 text-red-400 border border-red-500/20 font-medium disabled:opacity-40 transition-colors">
                {bulkBusy ? "Deleting…" : "Confirm Delete"}
              </button>
              <button onClick={() => setConfirmBulk(false)}
                className="px-4 py-2.5 rounded-xl text-white/50 hover:text-white border border-white/10 transition-colors">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminShell>
  );
}

function UserModal({ user, onClose, onStatusChange, onDelete }) {
  const src = user.profile_image || user.profileImage || user.avatar
    || user.avatar_url || user.image_url || user.photo_url
    || (Array.isArray(user.photos) ? user.photos[0] : null) || null;
  const [imgFailed,   setImgFailed]   = useState(false);
  const [acctStatus,  setAcctStatus]  = useState(user.account_status || "active");
  const [statusBusy,  setStatusBusy]  = useState(false);
  const [statusMsg,   setStatusMsg]   = useState("");
  const [privateSt,   setPrivateSt]   = useState(user.private_status || "none");
  const [privateMsg,  setPrivateMsg]  = useState("");
  const [privateBusy, setPrivateBusy] = useState(false);
  const [msgOpen,     setMsgOpen]     = useState(false);
  const [msgBody,     setMsgBody]     = useState("");
  const [msgSending,  setMsgSending]  = useState(false);
  const [msgResult,   setMsgResult]   = useState("");

  const initials = (user.name || "?").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const plan = user.plan || user.subscription_plan || "free";
  const prot = isProtected(user);

  const toggleStatus = async () => {
    const newStatus = acctStatus === "disabled" ? "active" : "disabled";
    setStatusBusy(true); setStatusMsg("");
    try {
      const token = getToken();
      const res = await fetch(`/api/admin/users/${user.id}/set-status`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Failed");
      setAcctStatus(newStatus);
      if (onStatusChange) onStatusChange(user.id, newStatus);
      setStatusMsg(newStatus === "disabled" ? "Account disabled." : "Account re-enabled.");
    } catch (e) { setStatusMsg("Error: " + e.message); }
    finally { setStatusBusy(false); }
  };

  const deleteUser = async () => {
    if (!window.confirm(`Permanently delete ${user.name || user.email}? This cannot be undone.`)) return;
    setStatusBusy(true); setStatusMsg("");
    try {
      const token = getToken();
      const res = await fetch(`/api/admin/users/${user.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Failed");
      if (onDelete) onDelete(user.id);
    } catch (e) { setStatusMsg("Error: " + e.message); setStatusBusy(false); }
  };

  const setPrivateApproval = async (action) => {
    setPrivateBusy(true); setPrivateMsg("");
    try {
      const token = getToken();
      const newStatus = action === "approve" ? "approved" : "none";
      const res = await fetch(`/api/admin/users/${user.id}/set-private-status`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ private_status: newStatus }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Failed");
      setPrivateSt(newStatus);
      setPrivateMsg(action === "approve" ? "Private access approved." : "Private access revoked.");
    } catch (e) { setPrivateMsg("Error: " + e.message); }
    finally { setPrivateBusy(false); }
  };

  const sendMessage = async () => {
    if (!msgBody.trim()) return;
    setMsgSending(true); setMsgResult("");
    try {
      const token = getToken();
      const res = await fetch(`/api/admin/concierge/${user.id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ body: msgBody.trim() }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Failed");
      setMsgResult("Message sent."); setMsgBody(""); setMsgOpen(false);
    } catch (e) { setMsgResult("Error: " + e.message); }
    finally { setMsgSending(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="glass rounded-3xl w-full max-w-md p-8 relative overflow-y-auto max-h-[90vh]">
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 text-white/60 hover:text-white transition-colors text-sm"
        >
          ✕
        </button>

        <div className="flex flex-col items-center mb-6">
          {src && !imgFailed ? (
            <img
              src={src}
              alt={user.name || "User"}
              onError={() => setImgFailed(true)}
              className="h-24 w-24 rounded-2xl object-cover ring-2 ring-[rgba(212,175,55,0.3)]"
            />
          ) : (
            <div className="h-24 w-24 rounded-2xl ring-2 ring-[rgba(212,175,55,0.3)] bg-[rgba(212,175,55,0.1)] flex items-center justify-center font-display text-2xl text-[#F2DE94]">
              {initials}
            </div>
          )}
          <div className="mt-4 font-display text-2xl">{user.name || "—"}</div>
          <div className="text-white/50 text-sm mt-1">{user.email || "—"}</div>
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <Detail label="Role">
            <span className={`text-[11px] px-2 py-0.5 rounded-full ${prot || user.role === "admin" ? "bg-[rgba(212,175,55,0.15)] text-[#F2DE94]" : "bg-white/5 text-white/50"}`}>
              {user.role || "user"}
            </span>
          </Detail>
          <Detail label="Status">
            <span className={`flex items-center gap-1.5 ${user.active ? "text-emerald-400" : "text-white/30"}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${user.active ? "bg-emerald-400" : "bg-white/20"}`} />
              {user.active ? "Active" : "Inactive"}
            </span>
          </Detail>
          {user.city       && <Detail label="City">{user.city}</Detail>}
          {user.state      && <Detail label="State">{user.state}</Detail>}
          {user.zip        && <Detail label="ZIP">{user.zip}</Detail>}
          {user.age        && <Detail label="Age">{user.age}</Detail>}
          {user.gender     && <Detail label="Gender" className="capitalize">{user.gender}</Detail>}
          {user.preference && <Detail label="Interested in" className="capitalize">{user.preference}</Detail>}
          <Detail label="Onboarding">
            <span className={user.onboarding_complete ? "text-emerald-400" : "text-white/30"}>
              {user.onboarding_complete ? "Complete" : "Pending"}
            </span>
          </Detail>
          {user.created_at && <Detail label="Joined">{user.created_at.slice(0, 10)}</Detail>}
          {user.updated_at && user.updated_at !== user.created_at && (
            <Detail label="Last Active">{user.updated_at.slice(0, 10)}</Detail>
          )}
        </div>

        <div className="mt-5 pt-4 border-t border-white/6">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] tracking-[0.2em] uppercase text-white/40">Account</div>
            <span className={`text-[11px] px-2 py-0.5 rounded-full ${
              acctStatus === "disabled" ? "bg-red-500/15 text-red-400" : "bg-emerald-500/15 text-emerald-400"
            }`}>{acctStatus === "disabled" ? "Disabled" : "Active"}</span>
          </div>
          {statusMsg && (
            <p className={`text-xs mb-2 ${statusMsg.startsWith("Error") ? "text-red-400" : "text-emerald-400"}`}>{statusMsg}</p>
          )}
          <button onClick={toggleStatus} disabled={statusBusy}
            className={`w-full rounded-xl py-2.5 text-sm font-medium transition-colors disabled:opacity-40 border ${
              acctStatus === "disabled"
                ? "bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border-emerald-500/20"
                : "bg-red-500/10 hover:bg-red-500/20 text-red-400 border-red-500/20"
            }`}>
            {statusBusy ? "Updating…" : acctStatus === "disabled" ? "Re-enable Account" : "Disable Account"}
          </button>
          {!prot && (
            <button onClick={deleteUser} disabled={statusBusy}
              className="w-full mt-2 rounded-xl py-2.5 text-sm font-medium bg-red-700/10 hover:bg-red-700/20 text-red-300 border border-red-700/20 disabled:opacity-40 transition-colors">
              {statusBusy ? "…" : "Delete User"}
            </button>
          )}
        </div>

        <div className="mt-5 pt-4 border-t border-white/6">
          <div className="text-[11px] tracking-[0.2em] uppercase text-white/40 mb-3">Membership</div>
          <div className="grid grid-cols-2 gap-2 text-xs mb-3">
            <div className="glass rounded-xl px-3 py-2">
              <div className="text-white/35 text-[10px] uppercase tracking-wider mb-1">Plan</div>
              <span className={`font-medium capitalize ${plan === "private" ? "text-[#F2DE94]" : "text-white/70"}`}>{plan}</span>
            </div>
            <div className="glass rounded-xl px-3 py-2">
              <div className="text-white/35 text-[10px] uppercase tracking-wider mb-1">Private</div>
              <span className={`font-medium capitalize ${
                privateSt === "approved" ? "text-emerald-400" :
                privateSt === "rejected" ? "text-red-400" :
                privateSt === "applied"  ? "text-yellow-400" : "text-white/40"
              }`}>{privateSt}</span>
            </div>
          </div>
          {privateMsg && (
            <p className={`text-xs mb-2 ${privateMsg.startsWith("Error") ? "text-red-400" : "text-emerald-400"}`}>{privateMsg}</p>
          )}
          <div className="flex gap-2">
            <button onClick={() => setPrivateApproval("approve")} disabled={privateBusy || privateSt === "approved"}
              className="flex-1 rounded-xl py-2 text-xs font-medium bg-[rgba(212,175,55,0.1)] hover:bg-[rgba(212,175,55,0.2)] text-[#F2DE94] border border-[rgba(212,175,55,0.2)] disabled:opacity-40 transition-colors">
              {privateBusy ? "…" : "Approve Private"}
            </button>
            <button onClick={() => setPrivateApproval("revoke")} disabled={privateBusy || privateSt === "none"}
              className="flex-1 rounded-xl py-2 text-xs font-medium bg-red-500/8 hover:bg-red-500/15 text-red-400 border border-red-500/20 disabled:opacity-40 transition-colors">
              {privateBusy ? "…" : "Revoke Private"}
            </button>
          </div>
        </div>

        <div className="mt-4">
          {msgResult && (
            <p className={`text-xs mb-2 ${msgResult.startsWith("Error") ? "text-red-400" : "text-emerald-400"}`}>{msgResult}</p>
          )}
          {msgOpen ? (
            <div className="space-y-2">
              <textarea
                value={msgBody}
                onChange={(e) => setMsgBody(e.target.value)}
                rows={3}
                placeholder="Write a concierge message…"
                className="w-full rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2 text-sm text-white placeholder:text-white/25 outline-none focus:border-[rgba(212,175,55,0.4)] resize-none"
              />
              <div className="flex gap-2">
                <button onClick={sendMessage} disabled={msgSending || !msgBody.trim()}
                  className="flex-1 rounded-xl py-2 text-xs font-medium bg-[rgba(212,175,55,0.15)] hover:bg-[rgba(212,175,55,0.25)] text-[#F2DE94] disabled:opacity-40 transition-colors">
                  {msgSending ? "Sending…" : "Send"}
                </button>
                <button onClick={() => { setMsgOpen(false); setMsgBody(""); }}
                  className="px-4 rounded-xl py-2 text-xs text-white/40 hover:text-white/70 transition-colors">
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button onClick={() => setMsgOpen(true)}
              className="w-full rounded-xl py-2.5 text-sm font-medium bg-white/[0.04] hover:bg-white/[0.08] text-white/70 border border-white/8 transition-colors">
              Message User
            </button>
          )}
        </div>

        {user.values?.length > 0 && (
          <div className="mt-5">
            <div className="text-[11px] tracking-[0.2em] uppercase text-white/40 mb-2">Values</div>
            <div className="flex flex-wrap gap-1.5">
              {user.values.map((v) => (
                <span key={v} className="text-xs px-2.5 py-1 rounded-full bg-[rgba(212,175,55,0.08)] border border-[rgba(212,175,55,0.2)] text-[#F2DE94]/80">
                  {v}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Detail({ label, children, className = "" }) {
  return (
    <div className="glass rounded-xl px-3 py-2.5">
      <div className="text-[10px] tracking-[0.18em] uppercase text-white/35 mb-1">{label}</div>
      <div className={`text-white/85 font-medium ${className}`}>{children}</div>
    </div>
  );
}
